import React, { FC, Fragment } from 'react';
import Page from 'evcharging/components/Page';
import StayUpdated from 'evcharging/containers/layout/StayUpdated';
import {
  BLOG_PAGE_SIZE,
  getBlogCategory, getCategoryById,
  getChildCategories,
  getGroupedPosts,
  getPostsByCategory
} from "evcharging/services/cmsClient";
import { createGetServerSideProps } from 'evcharging/app';
import { GetServerSidePropsContext } from 'next';
import { GroupedPosts, PostCategory, PostCollection } from "evcharging/services/types";
import Spacer from 'evcharging/components/Spacer';
import Section from 'evcharging/containers/layout/Section';
import Container from 'evcharging/components/Container';
import PostText from 'evcharging/containers/blog/PostText';
import { splitArray } from 'evcharging/utils/arrays';
import PostsRow from 'evcharging/containers/blog/PostsRow';
import BlogHeader from 'evcharging/containers/blog/BlogHeader';
import Error404 from 'pages/404';
import PaginationNextPrev from 'evcharging/components/PaginationNextPrev';
import { BLOG_CATEGORY_URL, NOT_FOUND_URL } from "evcharging/app/seo/urls";
import { MetadataProps } from 'evcharging/containers/layout/Metadata';
import E5Page from 'containers/common/sections/layout/Page';
import {I18nPage} from "i18n/types/pages.enum";
import GroupedArticles from "../../evcharging/containers/blog/GroupedArticles";
import BlogBreadcrumbs from "../../evcharging/containers/blog/BlogBreadcrumbs";

interface CategoryProps {
  category: PostCategory;
  parent: PostCategory;
  collection: PostCollection;
  page: number;
  metadata: MetadataProps;
  groupedPosts?: GroupedPosts[];
}

const Category: FC<CategoryProps> = (props) => {
  const { groupedPosts, category, parent, collection, page, metadata } = props;

  if (groupedPosts) {
    return (
      <Page {...metadata} locales={category.locales}>
        <E5Page withoutMetadata>
          <Spacer value={140} mobile={110} />
          <BlogBreadcrumbs parent={category} />
          <Spacer value={32} />
          <BlogHeader title={category.title} />
          <Spacer value={64} />
          {groupedPosts.map((data) => (
            <Fragment key={data.id}>
              <GroupedArticles data={data} />
              <Spacer value={64} mobile={48} />
            </Fragment>
          ))}
          <StayUpdated />
        </E5Page>
      </Page>
    )
  }

  if (!category) {
    return <Error404 />;
  }

  const { data, total } = collection;
  const [first, second, ...rest] = data;
  const top = [first, second].filter((item) => !!item);
  const chunks = splitArray(rest, 3);

  return (
    <Page {...metadata} locales={category.locales}>
      <E5Page withoutMetadata>
        <Spacer value={146} mobile={110} />
        <BlogBreadcrumbs parent={parent} category={category} />
        <Spacer value={32} />
        <BlogHeader title={category.title} />
        <Section>
          <Container>
            <PostsRow posts={top} columns={2} />
            {chunks.map((chunk, index) => (
              <Fragment key={index}>
                <Spacer value={64} mobile={0} />
                <PostsRow posts={chunk} />
              </Fragment>
            ))}
            <Spacer value={100} mobile={0} />
            <PaginationNextPrev page={page} size={BLOG_PAGE_SIZE} total={total} />
            <Spacer value={0} mobile={24} />
            <PostText>{category.description}</PostText>
          </Container>
        </Section>
        <StayUpdated />
      </E5Page>
    </Page>
  );
};

export const getServerSideProps = createGetServerSideProps(
  undefined,
  async (context: GetServerSidePropsContext, locale) => {
    const { query } = context;
    const { link, page = 1 } = query;

    const { category = null, i18nLinks = null } = await getBlogCategory(link ? String(link) : '', locale);

    if (!category) {
      return {
        redirect: {
          destination: NOT_FOUND_URL,
          permanent: true,
        },
      };
    }

    const parent = category.parent_id ? await getCategoryById(category.parent_id) : null;

    const metadata: MetadataProps = {};
    metadata.title = category.title.trim() + (query.page ? ` - Page ${query.page}` : '');
    metadata.description = category.meta_description || metadata.title;

    if (category.parent_id) {
      const collection = category
        ? await getPostsByCategory(locale, category.id, Number(page))
        : { data: [], total: 0 };

      if (Number(query.page) === 1) {
        return {
          redirect: {
            destination: BLOG_CATEGORY_URL(String(link)),
            permanent: false,
          },
        };
      }

      return {
        category,
        parent,
        collection,
        page: Number(page),
        metadata,
        i18nLinks,
      };
    } else {
      const children = await getChildCategories(category.id, locale);
      const groupedPosts = await getGroupedPosts(children, locale);

      return {
        category,
        parent,
        groupedPosts,
        metadata,
        i18nLinks,
      };
    }
  },
  [I18nPage.Blog, I18nPage.EnergyRates]
);

export default Category;
